import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import axios from 'axios';

interface PayrollSettings {
  payPeriodType: 'weekly' | 'biweekly' | 'monthly';
  payrollProcessingDay: number;
  defaultTaxRate: number;
  defaultBenefitsRate: number;
  automaticPayrollProcessing: boolean;
  emailNotifications: boolean;
  reportingCurrency: string;
  timeZone: string;
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<PayrollSettings>({
    payPeriodType: 'biweekly',
    payrollProcessingDay: 1,
    defaultTaxRate: 20,
    defaultBenefitsRate: 10,
    automaticPayrollProcessing: true,
    emailNotifications: true,
    reportingCurrency: 'USD',
    timeZone: 'America/New_York',
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      showSnackbar('Error fetching settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings', settings);
      showSnackbar('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      showSnackbar('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTextFieldChange = (field: keyof PayrollSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      [field]: field === 'payrollProcessingDay' || field === 'defaultTaxRate' || field === 'defaultBenefitsRate'
        ? Number(value)
        : value,
    }));
  };

  const handleSelectChange = (field: keyof PayrollSettings) => (
    event: SelectChangeEvent
  ) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSwitchChange = (field: keyof PayrollSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Settings</Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Payroll Configuration</Typography>
            <Box display="grid" gap={3}>
              <FormControl fullWidth>
                <InputLabel>Pay Period Type</InputLabel>
                <Select
                  value={settings.payPeriodType}
                  label="Pay Period Type"
                  onChange={handleSelectChange('payPeriodType')}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="biweekly">Bi-weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Payroll Processing Day"
                type="number"
                value={settings.payrollProcessingDay}
                onChange={handleTextFieldChange('payrollProcessingDay')}
                inputProps={{ min: 1, max: 31 }}
                fullWidth
              />

              <TextField
                label="Default Tax Rate (%)"
                type="number"
                value={settings.defaultTaxRate}
                onChange={handleTextFieldChange('defaultTaxRate')}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                fullWidth
              />

              <TextField
                label="Default Benefits Rate (%)"
                type="number"
                value={settings.defaultBenefitsRate}
                onChange={handleTextFieldChange('defaultBenefitsRate')}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                fullWidth
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>System Preferences</Typography>
            <Box display="grid" gap={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.automaticPayrollProcessing}
                    onChange={handleSwitchChange('automaticPayrollProcessing')}
                  />
                }
                label="Automatic Payroll Processing"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={handleSwitchChange('emailNotifications')}
                  />
                }
                label="Email Notifications"
              />

              <Divider />

              <FormControl fullWidth>
                <InputLabel>Reporting Currency</InputLabel>
                <Select
                  value={settings.reportingCurrency}
                  label="Reporting Currency"
                  onChange={handleSelectChange('reportingCurrency')}
                >
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Time Zone</InputLabel>
                <Select
                  value={settings.timeZone}
                  label="Time Zone"
                  onChange={handleSelectChange('timeZone')}
                >
                  <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                  <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings; 